import React, { useState } from "react";
import { Divider, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import StyledMenu from "../../../../components/layout/StyledMenu";
import { postNotification } from "../../../../components/utilities/Notifications";
import ExportModal from "../../../../components/modals/ExportModal";
import ProgressModal from "../../../../components/modals/ProgressModal";
import { useConfirm } from "material-ui-confirm";
import { BulkAddTestPlayersModal } from "./EventRosterModals";
import ModalLoadingMessage from "../../../../components/modals/ModalLoadingMessage";
import {
  postData,
  deleteData
} from "../../../../components/utilities/UtilityFunctions";
import { refreshRoster } from "../EventFunctions";
import { getEnvironmentVariable } from "../../../../global/environmentVariables";
import { adminToolingMenuItems } from "./AdminTooling";

export function OptionsMenu(props) {
  const { t: tGeneral } = useTranslation();

  const {
    anchorEl,
    setAnchorEl,
    setLoading,
    event,
    authContext,
    setBulkAddOpen,
    setBulkAddEmailsOpen,
    players,
    setRefreshPlayers,
    t
  } = props;

  const [testPlayersModal, setTestPlayersModal] = useState(false);
  const [rosterModal, setRosterModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(null);
  const [progress, setProgress] = useState(null);
  const confirm = useConfirm();
  const open = Boolean(anchorEl);
  return (
    <>
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          "aria-labelledby": "options-button"
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={async (e) => {
            setRosterModal(true);
            setAnchorEl(null);
          }}
          disableRipple
        >
          {t("exportRoster")}
        </MenuItem>
        {
          <MenuItem
            onClick={async (e) => {
              setLoading(true);

              try {
                await postData(`/events/${event.id}/sendListUploadRequest`, {
                  body: {
                    email: authContext?.authState?.identity?.email
                  }
                });

                postNotification(
                  "success",
                  tGeneral("notifications.success.uploadEmailsSent")
                );
              } catch (err) {
                postNotification("error", err);
              }

              setLoading(false);
              setAnchorEl(null);
            }}
            disableRipple
          >
            {t("sendListUploadEmails", {
              listLabel: event?.gameSystem?.listLabel || "List"
            })}
          </MenuItem>
        }

        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);
            var perc = 0;
            var notCheckedIn = players?.filter(
              (player) => !player.checkedIn && !player.dropped
            );
            if (!notCheckedIn?.length) {
              postNotification(
                "warning",
                tGeneral("notifications.warnings.noPlayersToCheckIn")
              );
              return;
            }
            try {
              await confirm({
                title: `Are you sure?`,
                description:
                  "Are you sure you wish to check in all non dropped players?",
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                },
                confirmationText: "Yes",
                cancellationText: "No"
              });
            } catch (err) {
              return;
            }

            setProgress(0);
            setProcessing(true);

            try {
              var step = 100 / notCheckedIn?.length;
              for (let player of notCheckedIn) {
                await postData(
                  `/${
                    event?.format?.teamEvent || event?.format?.doublesEvent
                      ? "teamplayers"
                      : "players"
                  }/${player?.id}`,
                  {
                    body: {
                      set: { checkedIn: true }
                    }
                  }
                );

                perc += step;
                setProgress(perc < 1 ? 1 : perc);
              }

              postNotification(
                "success",
                "All non dropped players have been checked in"
              );
            } catch (err) {
              postNotification("error", err);
            }
            if (perc !== 0) {
              setRefreshPlayers(true);
            }
            setProcessing(false);
            setProgress(null);
          }}
          disableRipple
        >
          {t("checkInAllPlayers")}
        </MenuItem>

        <Divider
          sx={{
            mt: 2,
            mb: 2
          }}
        />

        <MenuItem
          onClick={async () => {
            setAnchorEl(null);
            var perc = 0;
            var checkedIn = players?.filter(
              (player) => player.checkedIn && !player.dropped
            );
            if (!checkedIn?.length) {
              postNotification(
                "warning",
                tGeneral("notifications.warnings.noPlayersToCheckOut")
              );
              return;
            }
            try {
              await confirm({
                title: `Are you sure?`,
                description:
                  "Are you sure you wish to check out all non dropped players?",
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                },
                confirmationText: "Yes",
                cancellationText: "No"
              });
            } catch (err) {
              return;
            }

            setProgress(0);
            setProcessing(true);

            try {
              var step = 100 / checkedIn?.length;
              for (let player of checkedIn) {
                if (event?.format?.teamEvent || event?.format?.doublesEvent) {
                  await postData(`/teamplayers/${player?.id}`, {
                    body: {
                      set: { checkedIn: false }
                    }
                  });

                  if (player?.players && player?.players?.length) {
                    for (let singlePlayer of player?.players) {
                      if (!singlePlayer?.dropped) {
                        await postData(`/players/${singlePlayer?.id}`, {
                          body: {
                            set: { checkedIn: false }
                          }
                        });
                      }
                    }
                  }
                } else {
                  await postData(`/players/${player?.id}`, {
                    body: {
                      set: { checkedIn: false }
                    }
                  });
                }

                perc += step;
                setProgress(perc < 1 ? 1 : perc);
              }

              postNotification(
                "success",
                "All non dropped players have been checked out"
              );
            } catch (err) {
              postNotification("error", err);
            }
            if (perc !== 0) {
              setRefreshPlayers(true);
            }
            setProcessing(false);
            setProgress(null);
          }}
          disableRipple
        >
          {t("checkOutAllPlayers")}
        </MenuItem>

        {adminToolingMenuItems(
          t("adminTooling"),
          authContext?.authState?.identity?.isAdmin === true
            ? [
                {
                  key: "bulkAddPlayers",
                  label: t("bulkAddPlayers"),
                  onClick: () => {
                    setBulkAddOpen(true);
                    setAnchorEl(null);
                  }
                },
                {
                  key: "bulkAddPlayersEmail",
                  label: t("bulkAddPlayersEmail"),
                  onClick: () => {
                    setBulkAddEmailsOpen(true);
                    setAnchorEl(null);
                  }
                },
                {
                  key: "addTestPlayers",
                  label: t("addTestPlayers"),
                  onClick: () => {
                    setTestPlayersModal(true);
                    setAnchorEl(null);
                  }
                }
              ]
            : []
        )}

        {authContext?.authState?.identity?.isAdmin === true && (
          <Divider
            sx={{
              mt: 2,
              mb: 2
            }}
          />
        )}
        <MenuItem
          onClick={() => {
            refreshRoster();
          }}
        >
          {t("refreshRoster")}
        </MenuItem>
      </StyledMenu>
      <ExportModal
        label="Export Roster"
        exportTypes={["roster"]}
        event={event}
        setLoading={setLoading}
        open={rosterModal}
        setOpen={setRosterModal}
        authContext={authContext}
      />
      {authContext?.authState?.identity?.isAdmin === true && (
        <BulkAddTestPlayersModal
          open={testPlayersModal}
          setOpen={setTestPlayersModal}
          event={event}
          setRefreshPlayers={setRefreshPlayers}
          setLoadingMessage={setLoadingMessage}
        />
      )}
      <ProgressModal
        open={processing}
        value={progress}
        message="Players Checked In"
      />
      {loadingMessage && (
        <ModalLoadingMessage
          open={Boolean(loadingMessage)}
          message={loadingMessage}
        />
      )}
    </>
  );
}

export function FiltersMenu(props) {
  const {
    anchorEl,
    setAnchorEl,
    event,
    players,
    setFilteredPlayers,
    filterType,
    setFilterType,
    t
  } = props;

  const open = Boolean(anchorEl);

  return (
    <>
      <StyledMenu
        id="filters-menu"
        MenuListProps={{
          "aria-labelledby": "filters-button"
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          selected={filterType === "" ? true : false}
          onClick={async (e) => {
            setFilterType("");
            setAnchorEl(null);
          }}
          disableRipple
        >
          {event?.format?.teamEvent ? t("allTeams") : t("allPlayers")}
        </MenuItem>

        <Divider
          sx={{
            mt: 2,
            mb: 2
          }}
        />

        <MenuItem
          selected={filterType === "Checked In" ? true : false}
          onClick={async (e) => {
            setFilterType("Checked In");
            setAnchorEl(null);
          }}
          disableRipple
        >
          {event?.format?.teamEvent
            ? t("checkedInTeamsFilter")
            : t("checkedInPlayersFilter")}
        </MenuItem>

        <MenuItem
          selected={filterType === "Not Checked In" ? true : false}
          onClick={async (e) => {
            setFilterType("Not Checked In");
            setAnchorEl(null);
          }}
          disableRipple
        >
          {event?.format?.teamEvent
            ? t("notCheckedInTeamsFilter")
            : t("notCheckedInPlayersFilter")}
        </MenuItem>

        <Divider
          sx={{
            mt: 2,
            mb: 2
          }}
        />

        <MenuItem
          selected={filterType === "Lists" ? true : false}
          onClick={async (e) => {
            const newList = players?.filter((player) => player?.armyListText);
            setFilteredPlayers(newList);
            setFilterType("Lists");

            setAnchorEl(null);
          }}
          disableRipple
        >
          {event?.format?.teamEvent || event?.format?.doublesEvent
            ? t("teamsWithLists", {
                listLabel: event?.gameSystem?.listLabel || "List"
              })
            : t("playersWithLists", {
                listLabel: event?.gameSystem?.listLabel || "List"
              })}
        </MenuItem>
        <MenuItem
          selected={filterType === "No Lists" ? true : false}
          onClick={async (e) => {
            setFilterType("No Lists");
            setAnchorEl(null);
          }}
          disableRipple
        >
          {event?.format?.teamEvent || event?.format?.doublesEvent
            ? t("teamsWithoutLists", {
                listLabel: event?.gameSystem?.listLabel || "List"
              })
            : t("playersWithoutLists", {
                listLabel: event?.gameSystem?.listLabel || "List"
              })}
        </MenuItem>

        <Divider
          sx={{
            mt: 2,
            mb: 2
          }}
        />

        <MenuItem
          selected={filterType === "Dropped" ? true : false}
          onClick={async (e) => {
            setFilterType("Dropped");
            setAnchorEl(null);
          }}
          disableRipple
        >
          {event?.format?.teamEvent ? t("droppedTeams") : t("droppedPlayers")}
        </MenuItem>

        <MenuItem
          selected={filterType === "Not Dropped" ? true : false}
          onClick={async (e) => {
            setFilterType("Not Dropped");
            setAnchorEl(null);
          }}
          disableRipple
        >
          {event?.format?.teamEvent
            ? t("notDroppedTeams")
            : t("notDroppedPlayers")}
        </MenuItem>
      </StyledMenu>
    </>
  );
}

export function PlayerOptionsMenu(props) {
  const {
    captain,
    event,
    player,
    transaction,
    setLoading,
    anchorEl,
    setAnchorEl,
    setPlayer,
    setRefreshPlayers,
    setArmyModalOpen,
    setGroupInfoModalOpen,
    setSavedPlayer,
    setCaptainIsPlayer,
    setTeamPlayer,
    deletePlayer,
    showViewPlayer,
    t
  } = props;

  const open = Boolean(anchorEl);
  const confirm = useConfirm();
  const COMPANY_FLAG = getEnvironmentVariable("COMPANY_FLAG");

  return (
    <StyledMenu
      id="options-menu"
      MenuListProps={{
        "aria-labelledby": "options-button"
      }}
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
    >
      {showViewPlayer && (
        <>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setPlayer(player);
            }}
            disableRipple
          >
            {t("viewPlayer")}
          </MenuItem>
          <Divider sx={{ mt: 1, mb: 1 }} />
        </>
      )}

      <MenuItem
        onClick={async () => {
          setAnchorEl(null);
          try {
            await confirm({
              title: t("confirmRemove"),
              description: t("confirmRemoveDescription"),
              confirmationText: t("confirmationText.yes"),
              dialogProps: {
                sx: {
                  zIndex: 99999
                }
              }
            });
          } catch (err) {
            return;
          }

          if (player.transactionId && event?.chargeType === "square") {
            try {
              await confirm({
                title: t("noRefundAvailable"),
                description: t("noRefundAvailableDescription"),
                confirmationText: t("confirmationText.yes"),
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                }
              });
            } catch (err) {
              return;
            }
          } else if (player.transactionId && !transaction?.refunds?.length) {
            try {
              setLoading(true);
              await confirm({
                title: t("refundUser"),
                description: t("refundUserDescription"),
                confirmationText: t("confirmationText.yes"),
                cancellationText: t("confirmationText.no"),
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                }
              });

              await confirm({
                title: t("confirmationText.confirm"),
                description: t("refundUserConfirmDescription"),
                confirmationText: t("confirmationText.yes"),
                cancellationText: t("confirmationText.no"),
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                }
              });

              // refund the ticket...
              try {
                await postData(
                  `/transactions/${player.transactionId}/refundTransaction`
                );
              } catch (err) {
                setLoading(false);
                postNotification("error", err);
                return;
              }
            } catch (err) {}
          }

          try {
            setLoading(true);
            await deleteData(`/players/${player.id}`);
            if (setSavedPlayer) {
              setSavedPlayer({
                deleted: true,
                id: player.id
              });
            }
            if (deletePlayer) {
              deletePlayer(player.id);
            }
            setRefreshPlayers(true);
            setLoading(false);
            setPlayer(null);
            if (setCaptainIsPlayer && setTeamPlayer) {
              setCaptainIsPlayer(false);
              setTeamPlayer(null);
            }
          } catch (err) {
            setLoading(false);
            postNotification("error", err);
          }
        }}
        sx={{ color: "error.main" }}
        disableRipple
      >
        {captain ? t("deleteFromTeam") : t("deleteFromEvent")}
      </MenuItem>
      {!player.dropped ? (
        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);
            try {
              await confirm({
                title: t("confirmDrop"),
                description: t("confirmDropDescription"),
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                }
              });
            } catch (err) {
              return;
            }

            try {
              setLoading(true);

              let savedPlayer = await postData(`/players/${player.id}`, {
                body: {
                  set: {
                    dropped: true
                  }
                }
              });
              if (setSavedPlayer) {
                setSavedPlayer(savedPlayer);
              }
            } catch (err) {
              postNotification("error", err);
            }
            setRefreshPlayers(true);
            setLoading(false);
            setPlayer(null);
          }}
          disableRipple
          sx={{ color: "error.main" }}
        >
          {captain ? t("dropPlayerFromTeam") : t("dropPlayerFromEvent")}
        </MenuItem>
      ) : (
        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);

            try {
              setLoading(true);

              let savedPlayer = await postData(`/players/${player.id}`, {
                body: {
                  set: {
                    dropped: false
                  }
                }
              });
              if (setSavedPlayer) {
                setSavedPlayer(savedPlayer);
              }
            } catch (err) {
              postNotification("error", err);
            }
            setRefreshPlayers(true);
            setLoading(false);
            setPlayer(null);
          }}
          disableRipple
          sx={{ color: "error.main" }}
        >
          {captain ? t("undropPlayerFromTeam") : t("undropPlayerFromEvent")}
        </MenuItem>
      )}

      {player.transactionId &&
        !transaction?.refunds?.length &&
        event?.chargeType !== "square" && (
          <MenuItem
            onClick={async (e) => {
              setAnchorEl(null);
              try {
                await confirm({
                  title: t("confirmRefund"),
                  description: t("confirmRefundDescription"),
                  confirmationText: t("confirmationText.yes"),
                  dialogProps: {
                    sx: {
                      zIndex: 99999
                    }
                  }
                });
              } catch (err) {
                return;
              }

              try {
                setLoading(true);

                await postData(
                  `/transactions/${player.transactionId}/refundTransaction`
                );

                postNotification(
                  "success",
                  t("notifications.success.playerRefunded")
                );
                setLoading(false);
                setPlayer(null);
              } catch (err) {
                setLoading(false);
                postNotification("error", err);
              }
            }}
            sx={{ color: "error.main" }}
            disableRipple
          >
            {t("refundPlayer")}
          </MenuItem>
        )}

      {player.checkedIn && (
        <MenuItem
          onClick={async (e) => {
            setLoading(true);
            try {
              let savedPlayer = await postData(`/players/${player.id}`, {
                body: {
                  set: {
                    checkedIn: false
                  }
                }
              });
              if (setSavedPlayer) {
                setSavedPlayer(savedPlayer);
              }
            } catch (err) {
              postNotification("error", err);
            }
            setRefreshPlayers(true);
            setLoading(false);
            setPlayer(null);
            setAnchorEl(null);
          }}
          disableRipple
        >
          {t("undoCheckIn")}
        </MenuItem>
      )}

      {!captain && player?.listId && (
        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);
            try {
              await confirm({
                title: t("confirmDelete"),
                description: t("confirmDeleteDescription"),
                confirmationText: t("confirmationText.confirm"),
                cancellationText: t("confirmationText.cancel"),
                dialogProps: {
                  sx: {
                    zIndex: 99999
                  }
                }
              });
            } catch (err) {
              return;
            }

            try {
              setLoading(true);
              await deleteData(`/armylists/${player.listId}`);

              const updatedPlayer = { ...player };
              delete updatedPlayer.listId;

              if (setSavedPlayer) {
                setSavedPlayer(updatedPlayer);
              }

              postNotification("success", t("listDeletedSuccessfully"));
              setRefreshPlayers(true);
              setPlayer(null);
            } catch (err) {
              postNotification("error", err);
            }
            setLoading(false);
          }}
          sx={{ color: "error.main" }}
          disableRipple
        >
          {t("deleteList", {
            listLabel: event?.gameSystem?.listLabel || "List"
          })}
        </MenuItem>
      )}

      {!captain && (
        <>
          {COMPANY_FLAG !== "solforge" && (
            <MenuItem
              onClick={(e) => {
                setArmyModalOpen(true);
                setAnchorEl(null);
              }}
              disableRipple
            >
              {player?.armyListObjectId || player?.listId
                ? t("editList", {
                    listLabel: event?.gameSystem?.listLabel || "List"
                  })
                : t("addList", {
                    listLabel: event?.gameSystem?.listLabel || "List"
                  })}
            </MenuItem>
          )}
          {(player?.armyListObjectId || player?.listId) && (
            <MenuItem
              href={
                player.listUrl !== undefined && player.listUrl !== ""
                  ? player.listUrl
                  : player?.armyListSolforge?.id
                    ? `${
                        getEnvironmentVariable("HEADER_SOLFORGE_URL") || ""
                      }/fused/${player.armyListSolforge.id}`
                    : `/list/${player?.armyListObjectId || player?.listId}`
              }
              target="_blank"
              component="a"
              disableRipple
            >
              {t("viewList", {
                listLabel: event?.gameSystem?.listLabel || "List"
              })}
            </MenuItem>
          )}

          <MenuItem
            onClick={async (e) => {
              setLoading(true);
              try {
                await postData(`/players/${player.id}/sendListUploadRequest`);
                postNotification(
                  "success",
                  t("notifications.success.uploadEmailsSent")
                );
              } catch (err) {
                postNotification("error", err);
              }
              setLoading(false);
              setAnchorEl(null);
            }}
            disableRipple
          >
            {t("sendUploadEmail")}
          </MenuItem>
          <MenuItem
            onClick={async (e) => {
              setLoading(true);
              try {
                await postData(`/players/${player.id}/sendPlayerReceipt`);
                postNotification(
                  "success",
                  t("notifications.success.welcomeEmailSent")
                );
              } catch (err) {
                postNotification("error", err);
              }
              setLoading(false);
              setAnchorEl(null);
            }}
            disableRipple
          >
            {t("resendWelcomeEmail")}
          </MenuItem>
        </>
      )}

      {event?.gwoEvent || event?.wcwEvent ? (
        <>
          <MenuItem
            onClick={(e) => {
              setGroupInfoModalOpen(true);
              setAnchorEl(null);
            }}
            sx={{ color: "warning.main" }}
            disableRipple
          >
            {t("updateGroupInfo")}
          </MenuItem>
        </>
      ) : (
        <></>
      )}
    </StyledMenu>
  );
}
